/**
 * Per-user code draft persistence for the problem editor.
 *
 * Behavior:
 *   - On mount / when (problemId, language) changes:
 *       1. If signed in: fetch GET /api/drafts/:problemId?language=…
 *          If draft exists, return it. Otherwise fall back to starter.
 *       2. If signed out: always return starter code (no server state).
 *   - On every editor change:
 *       - Update in-memory state immediately.
 *       - If signed in: debounce (1.5s) and PUT to /api/drafts/:problemId.
 *         Don't block the UI; optimistic.
 *   - We do NOT save back the starter code — only actual user edits.
 *     We track whether the current value was set by the user (`edited`) or
 *     by the load path (not edited) and gate saves on `edited`.
 *
 * Designed to be drop-in for the ProblemDetailPage's useState(code) pair.
 */
import { useEffect, useRef, useState } from 'react';
import type { Language, ProblemDetail } from '@flarecode/shared';
import { LANGUAGE_LABELS } from '@flarecode/shared';
import { api, ApiError } from '../lib/api';

const SAVE_DEBOUNCE_MS = 1500;

export interface UseProblemDraftResult {
  /** Current editor value. Always a string (never undefined). */
  code: string;
  /** Call on every onChange from the editor. */
  setCode: (next: string) => void;
  /** True while the initial draft is loading. */
  isLoadingDraft: boolean;
  /** True if a save is in flight. Non-blocking; UI may show a subtle indicator. */
  isSaving: boolean;
  /** Timestamp of the last successful save, or null if none this session. */
  lastSavedAt: number | null;
}

export function useProblemDraft(
  problem: ProblemDetail | undefined,
  language: Language,
  userId: string | undefined,
): UseProblemDraftResult {
  const [code, setCodeInternal] = useState<string>('');
  const [isLoadingDraft, setIsLoadingDraft] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  // Track whether the current value came from user typing (as opposed to a
  // draft/starter load). Only "edited" values are written back to the server.
  const editedRef = useRef<boolean>(false);

  // Debounce timer handle.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cancel any in-flight save-timer across unmounts and language changes.
  const problemId = problem?.id;

  useEffect(() => {
    // Reset when the problem or language changes.
    if (!problem) return;
    let cancelled = false;

    editedRef.current = false;
    setIsLoadingDraft(true);

    async function load() {
      // Starter-code fallback, used both when unauthenticated and when no draft
      // exists on the server.
      const starter =
        problem!.defaultCode[language] ??
        `// No starter code for ${LANGUAGE_LABELS[language]} yet.`;

      if (!userId) {
        if (!cancelled) {
          setCodeInternal(starter);
          setIsLoadingDraft(false);
        }
        return;
      }

      try {
        const res = await api.getDraft(problem!.id, language);
        if (cancelled) return;
        if (res.source !== null) {
          setCodeInternal(res.source);
        } else {
          setCodeInternal(starter);
        }
      } catch (err) {
        // Draft fetch failing isn't catastrophic — fall back to starter.
        // 404s (problem not found by draft endpoint) also fall here; fine.
        if (!cancelled) {
          if (err instanceof ApiError) {
            console.warn('draft load failed', { status: err.status, code: err.code });
          }
          setCodeInternal(starter);
        }
      } finally {
        if (!cancelled) setIsLoadingDraft(false);
      }
    }
    load();

    return () => {
      cancelled = true;
      // Cancel pending save; we'll re-arm after the new problem's initial load
      // if the user edits again.
      if (saveTimerRef.current !== null) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemId, language, userId]);

  function setCode(next: string) {
    setCodeInternal(next);
    // Mark this value as user-edited; triggers save debouncing.
    editedRef.current = true;
    scheduleSave(next);
  }

  function scheduleSave(next: string) {
    if (!userId || !problem) return;
    if (saveTimerRef.current !== null) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void saveNow(next);
    }, SAVE_DEBOUNCE_MS);
  }

  async function saveNow(next: string) {
    if (!userId || !problem) return;
    setIsSaving(true);
    try {
      await api.saveDraft(problem.id, { language, source: next });
      setLastSavedAt(Date.now());
    } catch (err) {
      // Don't interrupt typing on save failure. Log and move on. Next edit
      // re-arms the debounce and tries again.
      if (err instanceof ApiError) {
        console.warn('draft save failed', { status: err.status, code: err.code });
      }
    } finally {
      setIsSaving(false);
    }
  }

  return { code, setCode, isLoadingDraft, isSaving, lastSavedAt };
}
